import { type JSONDocNode, JSONTransformer } from '@atlaskit/editor-json-transformer';
import { vec2 } from 'gl-matrix';
import { DOMParser } from 'prosemirror-model';

import { createEnhancedSchema } from '@canvas/adf/create-enhanced-schema';
import { textDefaultColor } from '@canvas/constants/colors/base';
import { defaultTextAlignment } from '@canvas/constants/elements/text';
import { type TextElementData, textElementType } from '@canvas/element-text';
import { boxCenter } from '@canvas/math/box';
import { type Board } from '@canvas/model/board';
import { type ElementData } from '@canvas/model/element';
import { type ConnectorElementData } from '@canvas/model/elements/connector';
import { connectorElementType } from '@canvas/model/elements/connector';
import { type PathElementData } from '@canvas/model/elements/path';
import { pathElementType } from '@canvas/model/elements/path';
import { pathLabelElementType } from '@canvas/model/elements/path-label';
import { smartConnectorElementType } from '@canvas/model/elements/smart-connector';
import { getPathSegmentsFromPoints } from '@canvas/path';
import { getBoundingBoxForPath } from '@canvas/path-utils/get-bounding-box-for-path';
import { Vector2 } from '@canvas/vector';

import { toConnectorStartAndEnd } from './connector-from-clipboard';

type SelectedElement = Omit<ElementData, 'id'>;

const isElementNegligibleForPositionCalculation = (element: SelectedElement) => {
  // Path label elements do not and should not impact the positions of paths.
  // This is because they are derived based on the endpoints/position of the
  // original path itself.
  return element.type !== pathLabelElementType;
};

export const getSelectionCenter = (
  selectedElements: SelectedElement[],
  elements: Board['elements'] = {}
): [number, number] => {
  const hasElements = Object.keys(elements).length > 0;

  const positions = selectedElements
    .filter(isElementNegligibleForPositionCalculation)
    .map((selectedElement) => {
      if (selectedElement.type === pathElementType) {
        const path = selectedElement as PathElementData;
        const pathBox = getBoundingBoxForPath(
          path.start,
          path.segments,
          path.stroke,
          path.startCap,
          path.endCap
        );
        const centre = boxCenter(pathBox);
        return Vector2.fromVec2(centre);
      }

      if (
        hasElements &&
        (selectedElement.type === connectorElementType ||
          selectedElement.type === smartConnectorElementType)
      ) {
        const connector = selectedElement as ConnectorElementData;

        const { start, end } = toConnectorStartAndEnd(connector, elements);
        const segments = getPathSegmentsFromPoints(start, end);

        const connectorBox = getBoundingBoxForPath(
          start,
          segments,
          connector.stroke,
          connector.startCap,
          connector.endCap
        );
        const centre = boxCenter(connectorBox);

        return Vector2.fromVec2(centre);
      }

      return selectedElement.position;
    });

  const allX = positions.map((p) => p.x);
  const allY = positions.map((p) => p.y);
  const maxX = Math.max(...allX);
  const maxY = Math.max(...allY);
  const minX = Math.min(...allX);
  const minY = Math.min(...allY);

  const centerX = (maxX - minX) / 2 + minX;
  const centerY = (maxY - minY) / 2 + minY;

  return [centerX, centerY];
};

export const getRelativeCoordinate = (origin: vec2, coord: vec2): vec2 => {
  return vec2.sub([0, 0], coord, origin);
};

const textElementDataFromAdfDoc = (adfDoc: JSONDocNode): Omit<TextElementData, 'id'> => ({
  type: textElementType,
  size: new Vector2(1, 1),
  text: JSON.stringify(adfDoc),
  position: new Vector2(0, 0),
  allowFlexibleWidth: true,
  color: textDefaultColor,
  basisSize: new Vector2(1, 1),
  basisPosition: new Vector2(0, 0),
  alignment: defaultTextAlignment,
});

// Placed outside to avoid re-creating the transformer and schema on every call
const transformer = new JSONTransformer();
// Create a custom schema with enhanced marks
const customSchema = createEnhancedSchema();
// Create a DOM serializer from our enhanced schema (DOM -> ProseMirror)
const domParser = DOMParser.fromSchema(customSchema);
export const handleFormattedText = (content: string) => {
  try {
    // Parses HTML string into a DOM object
    const htmlDoc = new globalThis.DOMParser().parseFromString(content, 'text/html');
    const bodyElement = htmlDoc.body;
    if (!bodyElement) {
      return undefined;
    }
    // Convert inline images to links: create <a> tags with valid href=src and text=alt
    // so that we can keep some semblance of the image when pasting.
    // Currently, we cannot parse inline images copied from Confluence pages since they
    // are of type 'media' and 'mediaSingle' which we do not have the itemization for.
    const imgTags = Array.from(bodyElement.querySelectorAll('img'));
    imgTags.forEach((img) => {
      const altText = img.getAttribute('alt');
      const src = img.getAttribute('src');
      if (altText && src) {
        const linkElement = htmlDoc.createElement('a');
        linkElement.setAttribute('href', src);
        linkElement.textContent = altText;
        img.replaceWith(linkElement);
      } else if (altText) {
        const textNode = htmlDoc.createTextNode(altText);
        img.replaceWith(textNode);
      } else {
        img.remove();
      }
    });
    // We remove blockquote from the html content as we do not have the itemization for
    // blockquotes and currently does not render the whole paragraph even with adjacent
    // valid nodes.
    // Remove the code above when we have the itemization for blockquotes.
    const blockquoteTags = Array.from(bodyElement.querySelectorAll('blockquote'));
    blockquoteTags.forEach((blockquote) => {
      const parent = blockquote.parentNode;
      if (parent) {
        while (blockquote.firstChild) {
          parent.insertBefore(blockquote.firstChild, blockquote);
        }
        parent.removeChild(blockquote);
      }
    });

    // Parse DOM object to a ProseMirror node to obtain ADF doc
    const pmNode = domParser.parse(bodyElement);
    const adfDoc = transformer.encode(pmNode);

    const elementData = [textElementDataFromAdfDoc(adfDoc) as TextElementData];
    return elementData;
  } catch (error) {
    // This is fine as it will eventually default to plain text if the ADF is invalid
    return undefined;
  }
};
